import { getProductConfig } from '@/app/config/products.config';
import { useRequirePermissions } from '@/shared/hooks';
import { Page, PageHeader } from '@/shared/ui/page';
import { isInspectionTerminalStatus } from '@/shared/utils';
import { TabsComponent, Tag } from '@noorden-insurtech/alba-design-system';
import { Home } from 'lucide-react';
import { useState } from 'react';
import { useProposalDetails } from '../../quotes/hooks/homeowner/use-proposal-details.hook';
import { useRecordsInspection } from '../hooks/use-records-inspection.hook';
import { Step1Overview } from './inspection-steps/step-1-overview';
import { Step2Inspection } from './inspection-steps/step-2-inspection';
import { Step3Interactions } from './inspection-steps/step-3-interactions';

interface InspectionDetailsProps {
  proposalId: string;
  inspectionId?: string;
  initialTab?: 'overview' | 'inspection' | 'interactions';
  productName?: string;
}

export function InspectionDetails({
  proposalId,
  inspectionId: inspectionIdProp,
  initialTab = 'overview',
  productName,
}: InspectionDetailsProps) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const {
    data: proposalDetails,
    isLoading,
    error,
  } = useProposalDetails(proposalId);

  const risk = proposalDetails?.lobs?.[0]?.risks?.[0] as
    | (Record<string, unknown> & {
        inspections?: Array<{ id?: string }>;
      })
    | undefined;
  const inspections = risk?.inspections ?? [];
  const latestInspectionId = inspections[inspections.length - 1]?.id ?? '';

  const effectiveProp =
    inspectionIdProp && inspectionIdProp !== '_' ? inspectionIdProp : undefined;

  const inspectionId = (() => {
    // Enquanto os detalhes ainda estão carregando, confiar no prop da URL.
    if (effectiveProp && (isLoading || inspections.length === 0)) {
      return effectiveProp;
    }
    if (effectiveProp) {
      const isValidInspection = inspections.some(
        (i: { id?: string }) => i.id === effectiveProp
      );
      if (isValidInspection) return effectiveProp;
    }
    return latestInspectionId;
  })();

  const { data: inspectionRecords } = useRecordsInspection(
    proposalId,
    inspectionId,
    { enabled: Boolean(proposalId?.trim() && inspectionId?.trim()) }
  );
  const latestRecordStatus = inspectionRecords?.[0]?.status;
  const isInspectionTerminal = isInspectionTerminalStatus(latestRecordStatus);

  const { hasPermission: canWrite } = useRequirePermissions({
    permissions: ['policies.inspections.write'],
  });
  const canEditInspection = (canWrite ?? false) && !isInspectionTerminal;

  const resolvedProductName =
    productName ??
    getProductConfig(proposalDetails?.productCode ?? '')?.displayName ??
    '';
  const title = resolvedProductName
    ? `Inspeção - ${resolvedProductName}`
    : 'Inspeção';

  return (
    <Page.Root>
      <Page.Breadcrumb
        items={[
          {
            label: 'Página Inicial',
            href: '/dashboard',
            iconLeft: <Home className='h-4 w-4' />,
          },
          { label: 'Subscrição' },
          { label: 'Propostas em Inspeção', href: '/inspections' },
          { label: 'Inspeção', active: true },
        ]}
      />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>{title}</PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados e registros da inspeção para validação da
              proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>

          <PageHeader.Actions>
            <Tag.Root colorScheme='lightGreen'>
              <Tag.Label>28 dias restantes</Tag.Label>
            </Tag.Root>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='py-4'>
        <TabsComponent.Root
          value={activeTab}
          onValueChange={details => setActiveTab(details.value)}
        >
          <TabsComponent.List className='grid grid-cols-3 overflow-hidden rounded-2xl'>
            <TabsComponent.Trigger
              value='overview'
              className='min-h-[72px] px-2 py-3 text-center text-sm leading-tight'
            >
              Resumo da Proposta
            </TabsComponent.Trigger>

            <TabsComponent.Trigger
              value='inspection'
              className='min-h-[72px] px-2 py-3 text-center text-sm leading-tight'
            >
              Acompanhamento da Inspeção
            </TabsComponent.Trigger>

            <TabsComponent.Trigger
              value='interactions'
              className='min-h-[72px] px-2 py-3 text-center text-sm leading-tight'
            >
              Registro de Interações
            </TabsComponent.Trigger>
          </TabsComponent.List>

          <TabsComponent.Content value='overview'>
            <Step1Overview
              inspectionId={inspectionId}
              proposalId={proposalId}
              proposalDetails={proposalDetails}
              isLoading={isLoading}
              error={error}
            />
          </TabsComponent.Content>

          <TabsComponent.Content value='inspection'>
            <Step2Inspection
              policyId={proposalId}
              inspectionId={inspectionId}
              canWrite={canEditInspection}
              hasWritePermission={canWrite ?? false}
            />
          </TabsComponent.Content>

          <TabsComponent.Content value='interactions'>
            <Step3Interactions
              policyId={proposalId}
              inspectionId={inspectionId}
            />
          </TabsComponent.Content>
        </TabsComponent.Root>
      </Page.Content>
    </Page.Root>
  );
}
